import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import { fosError, fosFunds, formatDate, INTERACTION_LABEL, type FollowUpBucket, type FollowUpRow, type FollowUpsView } from '../../lib/fosApi';
import { useFos } from './FosShell';
import { Kpi } from './Progress';
import { EmptyState, ErrorNote, Icon, Section } from './ui';

const BUCKETS: Array<{ key: FollowUpBucket; title: string; tone?: 'gold' | 'good' | 'muted' }> = [
  { key: 'overdue', title: 'Overdue' }, { key: 'today', title: 'Today', tone: 'gold' }, { key: 'week', title: 'Next 7 days', tone: 'good' }, { key: 'later', title: 'Later', tone: 'muted' },
];
const ICON: Record<string, string> = { call: 'phone', email: 'mail', visit: 'pin', meeting: 'users', event: 'heart', note: 'note', follow_up: 'clock' };

function dueText(r: FollowUpRow): string {
  if (r.bucket === 'today') return 'Due today';
  if (r.daysFromToday < 0) return `${-r.daysFromToday} day${r.daysFromToday === -1 ? '' : 's'} overdue`;
  return r.daysFromToday === 1 ? 'Due tomorrow' : `Due in ${r.daysFromToday} days`;
}

export default function FosFollowUps() {
  const { context } = useFos();
  const [filter, setFilter] = useState<'all' | 'mine'>('all');
  const [data, setData] = useState<FollowUpsView | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false; setError('');
    fosFunds.followUps({ filter }).then((d) => { if (!cancelled) setData(d); }).catch((cause) => { if (!cancelled) setError(fosError(cause).message); });
    return () => { cancelled = true; };
  }, [filter]);

  if (!context.capabilities.viewFollowUps) return <Section title="Follow-ups" icon="clock"><EmptyState icon="lock" title="Follow-ups are restricted">You do not have access to donor activity.</EmptyState></Section>;
  if (!data && !error) return <div className="fos-loading"><Spinner label="Loading follow-ups" /></div>;
  return (
    <>
      <div className="fos-toolbar">
        <div className="fos-seg" role="group" aria-label="Show">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Everyone</button>
          {context.personId && <button className={filter === 'mine' ? 'active' : ''} onClick={() => setFilter('mine')}>Assigned to me</button>}
        </div>
        {data && <span className="fos-count subtitle">As of {formatDate(data.today)}</span>}
      </div>
      <ErrorNote message={error} />
      {data && (
        <>
          <div className="fos-kpis">
            {BUCKETS.map((b) => <Kpi key={b.key} label={b.title} value={String(data.counts[b.key])} tone={b.key === 'overdue' ? undefined : b.tone} />)}
          </div>
          {data.rows.length === 0 ? (
            <Section title="Follow-ups" icon="clock"><EmptyState icon="check" title={filter === 'mine' ? 'Nothing assigned to you' : 'No follow-ups planned'}>
              Record the next step when you log a call, visit or meeting on a donor's page, and it appears here on its date.</EmptyState></Section>
          ) : BUCKETS.map((b) => {
            const rows = data.rows.filter((r) => r.bucket === b.key);
            if (!rows.length) return null;
            return (
              <Section key={b.key} title={`${b.title} (${data.counts[b.key]})`} icon={b.key === 'overdue' ? 'alert' : 'clock'} className={b.key === 'overdue' ? 'fos-overdue' : ''}>
                <ul className="fos-followups">
                  {rows.map((r) => (
                    <li key={r.relationshipId}>
                      <span className="fos-followup-icon"><Icon name={ICON[r.type ?? 'follow_up'] ?? 'clock'} size={16} /></span>
                      <div className="fos-followup-main">
                        <Link to={`/fos/donors/${r.donorId}`} className="fos-donor-name">{r.donorName}</Link>
                        <div className="subtitle">{r.type ? INTERACTION_LABEL[r.type] : 'Follow up'} · {r.scopeLabel}{r.coordinatorName ? ` · ${r.coordinatorName}` : ' · no coordinator'}</div>
                      </div>
                      <div className="fos-followup-due"><strong className={r.bucket === 'overdue' ? 'owed' : ''}>{dueText(r)}</strong><small>{formatDate(r.dueOn)}</small></div>
                      <Link className="btn small" to={`/fos/donors/${r.donorId}`}>Open</Link>
                    </li>
                  ))}
                </ul>
              </Section>
            );
          })}
          {data.truncated && <p className="fos-hint">Showing the first 300 follow-ups.</p>}
        </>
      )}
    </>
  );
}
